import type { Directive } from "vue";

import { useTooltipStore } from "@/store/tooltipStore";

interface TooltipElement extends HTMLElement {
  _preventContextMenu?: (e: Event) => void;
  _listeners?: Record<string, EventListener>;
  _showTimeout?: ReturnType<typeof setTimeout> | null;
}

export const tooltip: Directive = {
  mounted(el: TooltipElement, binding) {
    if (!binding.value) return;

    el.dataset.tooltipId = String(binding.value);

    const tooltipStore = useTooltipStore();
    const recordId = binding.value;
    const isMobile = navigator.maxTouchPoints > 0;
    const delay = isMobile ? 500 : 150;

    el._listeners = {};

    const showTooltip = () => {
      void tooltipStore.show({ recordId, target: el });
    };

    const scheduleHide = () => {
      tooltipStore.hide();
    };

    if (isMobile) {
      const preventContextMenu = (e: Event) => e.preventDefault();
      el.addEventListener("contextmenu", preventContextMenu);
      el._preventContextMenu = preventContextMenu;

      let touchMoved = false;

      const onTouchStart = (e: Event) => {
        touchMoved = false;
        el._showTimeout = setTimeout(() => {
          if (!touchMoved) {
            e.preventDefault();
            showTooltip();
          }
        }, delay);
      };
      const onTouchMove = () => {
        touchMoved = true;
        if (el._showTimeout) clearTimeout(el._showTimeout);
      };
      const onTouchEnd = (e: Event) => {
        if (el._showTimeout) clearTimeout(el._showTimeout);
        if (touchMoved || !tooltipStore.visible) return;
        e.preventDefault();
      };

      el.addEventListener("touchstart", onTouchStart);
      el.addEventListener("touchmove", onTouchMove);
      el.addEventListener("touchend", onTouchEnd);

      el._listeners.touchstart = onTouchStart;
      el._listeners.touchmove = onTouchMove;
      el._listeners.touchend = onTouchEnd;
    } else {
      const onMouseEnter = () => {
        el._showTimeout = setTimeout(showTooltip, delay);
      };
      const onMouseLeave = () => {
        if (el._showTimeout) clearTimeout(el._showTimeout);
        scheduleHide();
      };
      // Clicking before the delay elapses shouldn't still pop the preview open afterward.
      const onMouseDown = () => {
        if (el._showTimeout) clearTimeout(el._showTimeout);
      };

      el.addEventListener("mouseenter", onMouseEnter);
      el.addEventListener("mouseleave", onMouseLeave);
      el.addEventListener("mousedown", onMouseDown);

      el._listeners.mouseenter = onMouseEnter;
      el._listeners.mouseleave = onMouseLeave;
      el._listeners.mousedown = onMouseDown;
    }
  },

  unmounted(el: TooltipElement) {
    delete el.dataset.tooltipId;

    if (el._showTimeout) {
      clearTimeout(el._showTimeout);
      delete el._showTimeout;
    }

    if (el._preventContextMenu) {
      el.removeEventListener("contextmenu", el._preventContextMenu);
      delete el._preventContextMenu;
    }

    if (el._listeners) {
      for (const [event, handler] of Object.entries(el._listeners)) {
        el.removeEventListener(event, handler);
      }
      delete el._listeners;
    }
  },
};
